import { FC, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import { TbMapPinFilled } from 'react-icons/tb';
import Map, { MapRef, Marker } from 'react-map-gl/maplibre';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import {
    createThemedMapStyle,
    ThemedMapControls,
    ThemedMapPopup,
} from '@/shared/components/data-visualization/maplibre';
import {
    useReportData,
    useReportQuery,
} from '@/shared/components/modals/ResourceActionModals/EditReportComponentModal/helpers/hook';
import { ReportComponent } from '@/types';
import { MapConfig } from '@/types/api/modules/business-intelligence';

import 'maplibre-gl/dist/maplibre-gl.css';

interface MapReportComponentProps {
    component: ReportComponent;
}

type MapPoint = {
    id: string;
    label: string;
    latitude: number;
    longitude: number;
    metric?: number;
};

const LATITUDE_FIELD_NAMES = ['latitude', 'lat'];
const LONGITUDE_FIELD_NAMES = ['longitude', 'lng', 'lon', 'long'];

const findField = (rows: Record<string, unknown>[], preferred: string | undefined, candidates: string[]) => {
    if (preferred) {
        return preferred;
    }

    const fields = Object.keys(rows[0] ?? {});

    return fields.find((field) => candidates.includes(field.toLowerCase()));
};

const MapReportComponent: FC<MapReportComponentProps> = ({ component }): JSX.Element => {
    const { token, resolvedThemeName } = useAntDesignTheme();
    const config = component.configuration as Partial<MapConfig> | undefined;
    const mapRef = useRef<MapRef | null>(null);
    const query = useReportQuery(component);
    const { data } = useReportData(component);

    const mapStyle =
        resolvedThemeName === 'dark'
            ? 'https://tiles.openfreemap.org/styles/dark'
            : 'https://tiles.openfreemap.org/styles/positron';

    const latitudeField = findField(
        data.rows,
        config?.latitudeField ?? query.dimensions[0]?.field,
        LATITUDE_FIELD_NAMES,
    );
    const longitudeField = findField(
        data.rows,
        config?.longitudeField ?? query.dimensions[1]?.field,
        LONGITUDE_FIELD_NAMES,
    );
    const metricField = config?.metric?.field ?? query.metrics[0]?.field;
    const labelField = query.dimensions.find(
        (dimension) => ![latitudeField, longitudeField].includes(dimension.field),
    )?.field;
    const points = data.rows.reduce<MapPoint[]>((result, row, index) => {
        const latitude = Number(latitudeField ? row[latitudeField] : undefined);
        const longitude = Number(longitudeField ? row[longitudeField] : undefined);

        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
            return result;
        }

        const metric = Number(metricField ? row[metricField] : undefined);

        result.push({
            id: `${latitude}-${longitude}-${index}`,
            label: String((labelField && row[labelField]) || `Location ${index + 1}`),
            latitude,
            longitude,
            ...(Number.isFinite(metric) ? { metric } : {}),
        });

        return result;
    }, []);

    const [popupInfo, setPopupInfo] = useState<MapPoint | null>(null);
    const center = config?.map?.center ?? [-98, 38];

    return (
        <div
            className={`cc-themed-map${resolvedThemeName === 'dark' ? ' cc-themed-map--dark' : ''}`}
            style={{
                height: '100%',
                width: '100%',
            }}
        >
            <Map
                attributionControl={false}
                initialViewState={{
                    longitude: center[0],
                    latitude: center[1],
                    zoom: config?.map?.zoom ?? 2,
                }}
                mapStyle={mapStyle}
                ref={mapRef}
                style={createThemedMapStyle(token, {
                    height: '100%',
                    width: '100%',
                })}
                reuseMaps
                mapLib={maplibregl}
            >
                <ThemedMapControls
                    mapRef={mapRef}
                    onReset={() =>
                        mapRef.current?.getMap().flyTo({
                            center,
                            duration: 650,
                            zoom: config?.map?.zoom ?? 2,
                        })
                    }
                />

                {points.map((point) => (
                    <Marker
                        key={point.id}
                        longitude={point.longitude}
                        latitude={point.latitude}
                        anchor='center'
                        onClick={(event) => {
                            event.originalEvent.stopPropagation();
                            setPopupInfo(point);
                        }}
                    >
                        <TbMapPinFilled
                            aria-label={point.label}
                            size={
                                point.metric
                                    ? `${Math.min(24, 14 + Math.log10(Math.abs(point.metric) + 1) * 2)}px`
                                    : '1rem'
                            }
                            style={{ color: token.blue6 }}
                        />
                    </Marker>
                ))}

                {popupInfo ? (
                    <ThemedMapPopup
                        icon={<TbMapPinFilled />}
                        items={
                            popupInfo.metric !== undefined
                                ? [
                                      {
                                          label: query.metrics[0]?.label ?? metricField ?? 'Metric',
                                          value: popupInfo.metric.toLocaleString(),
                                      },
                                  ]
                                : []
                        }
                        latitude={popupInfo.latitude}
                        longitude={popupInfo.longitude}
                        onClose={() => setPopupInfo(null)}
                        title={popupInfo.label}
                    />
                ) : null}
            </Map>
        </div>
    );
};

export default MapReportComponent;
